import React from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  AssetType,
  CreativeFilters,
  GroupBy,
  MediaFilter,
} from '../types';
import {
  ASSET_TYPE_LABELS,
  DATE_PRESETS,
  ExportScope,
  GROUP_BY_LABELS,
  MEDIA_LABELS,
  fmtInt,
  isoDaysAgo,
  isoToday,
} from '../utils';

interface Props {
  filters: CreativeFilters;
  onChange: (patch: Partial<CreativeFilters>) => void;
  onReset: () => void;
  onOpenColumns: () => void;
  onAddFilter: () => void;
  onRemoveRule: (index: number) => void;
  onClearRules: () => void;
  ruleLabels: string[];
  hiddenColumnCount: number;
  onExport: (scope: ExportScope) => void;
  // What each choice would actually write, so the menu says it rather than
  // leaving "this page" to be discovered by opening the file.
  pageRowCount: number;
  totalRowCount: number;
  disabled?: boolean;
}

const MEDIA_OPTIONS: MediaFilter[] = ['all', 'video', 'static', 'carousel'];
const GROUP_OPTIONS: GroupBy[] = ['ad', 'creative', 'headline', 'primary_text'];
const ASSET_OPTIONS: AssetType[] = ['headline', 'primary_text', 'image', 'video'];
const labelSx = { color: '#64748B', mr: 0.5, alignSelf: 'center' } as const;

export default function FilterBar({
  filters,
  onChange,
  onReset,
  onOpenColumns,
  onAddFilter,
  onRemoveRule,
  onClearRules,
  ruleLabels,
  hiddenColumnCount,
  onExport,
  pageRowCount,
  totalRowCount,
  disabled,
}: Props) {
  const [searchDraft, setSearchDraft] = React.useState(filters.search);
  const [exportAnchor, setExportAnchor] = React.useState<HTMLElement | null>(null);

  const runExport = (scope: ExportScope) => {
    setExportAnchor(null);
    onExport(scope);
  };

  // Keep the box in step when filters change from outside (back/forward, reset).
  React.useEffect(() => setSearchDraft(filters.search), [filters.search]);

  const activePreset = DATE_PRESETS.find(
    preset => filters.start === isoDaysAgo(preset.days - 1) && filters.end === isoToday(),
  );

  const commitSearch = () => {
    if (searchDraft !== filters.search) onChange({ search: searchDraft });
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="caption" sx={labelSx}>
            Range
          </Typography>
          <ButtonGroup size="small" disabled={disabled}>
            {DATE_PRESETS.map(preset => (
              <Button
                key={preset.label}
                variant={activePreset?.days === preset.days ? 'contained' : 'outlined'}
                onClick={() =>
                  onChange({ start: isoDaysAgo(preset.days - 1), end: isoToday() })
                }
              >
                {preset.label}
              </Button>
            ))}
          </ButtonGroup>
          <TextField
            type="date"
            size="small"
            value={filters.start}
            disabled={disabled}
            onChange={event => onChange({ start: event.target.value })}
            sx={{ width: 150 }}
            inputProps={{ 'aria-label': 'Start date' }}
          />
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            to
          </Typography>
          <TextField
            type="date"
            size="small"
            value={filters.end}
            disabled={disabled}
            onChange={event => onChange({ end: event.target.value })}
            sx={{ width: 150 }}
            inputProps={{ 'aria-label': 'End date' }}
          />
        </Box>


      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <Typography variant="caption" sx={labelSx}>
          Group
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filters.level}
          disabled={disabled}
          onChange={(_, value) => value && onChange({ level: value })}
        >
          <Tooltip title="One row per ad (or per rollup of ads). Carries true revenue and profit from Triple Whale.">
            <ToggleButton value="ad">Ad level</ToggleButton>
          </Tooltip>
          <Tooltip title="One row per individual headline, body, image or video, with the spend Meta actually attributed to it across every ad that rotated it. Meta-reported metrics only.">
            <ToggleButton value="asset">Asset level</ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>

        {filters.level === 'asset' ? (
          <TextField
            select
            size="small"
            label="Asset"
            value={filters.assetType}
            disabled={disabled}
            onChange={event =>
              onChange({ assetType: event.target.value as AssetType })
            }
            sx={{ width: 170 }}
          >
            {ASSET_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>
                {ASSET_TYPE_LABELS[option]}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            select
            size="small"
            label="Group by"
            value={filters.groupBy}
            disabled={disabled}
            onChange={event => onChange({ groupBy: event.target.value as GroupBy })}
            sx={{ width: 170 }}
          >
            {GROUP_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>
                {GROUP_BY_LABELS[option]}
              </MenuItem>
            ))}
          </TextField>
        )}




      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search name, headline, copy"
          value={searchDraft}
          disabled={disabled}
          onChange={event => setSearchDraft(event.target.value)}
          onBlur={commitSearch}
          onKeyDown={event => {
            if (event.key === 'Enter') commitSearch();
          }}
          sx={{ width: 250 }}
        />
        <Box
          sx={{
            display: filters.level === 'asset' ? 'none' : 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Typography variant="caption" sx={labelSx}>
            Media
          </Typography>
          {MEDIA_OPTIONS.map(option => (
            <Chip
              key={option}
              label={option === 'all' ? 'All' : MEDIA_LABELS[option]}
              size="small"
              disabled={disabled}
              onClick={() => onChange({ mediaType: option })}
              variant={filters.mediaType === option ? 'filled' : 'outlined'}
              sx={
                filters.mediaType === option
                  ? { backgroundColor: 'rgba(99,102,241,0.12)', color: '#4F46E5' }
                  : undefined
              }
            />
          ))}
        </Box>

        <Box sx={{ flex: 1 }} />

        <Button size="small" variant="outlined" onClick={onAddFilter} disabled={disabled}>
          Add filter
        </Button>

        <Button size="small" variant="outlined" onClick={onOpenColumns} disabled={disabled}>
          Columns{hiddenColumnCount > 0 ? ` (${hiddenColumnCount} hidden)` : ''}
        </Button>

        <Tooltip title="Download the table as CSV — the columns you have showing, with raw numbers rather than the formatted ones on screen.">
          {/* A disabled button doesn't fire the events a Tooltip listens for, so
              the span is what keeps the explanation reachable when there is
              nothing to export. */}
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={event => setExportAnchor(event.currentTarget)}
              disabled={disabled || totalRowCount === 0}
            >
              Export
            </Button>
          </span>
        </Tooltip>

        <Menu
          anchorEl={exportAnchor}
          open={!!exportAnchor}
          onClose={() => setExportAnchor(null)}
        >
          <MenuItem onClick={() => runExport('page')}>
            This page ({fmtInt(pageRowCount)} row{pageRowCount === 1 ? '' : 's'})
          </MenuItem>
          <MenuItem onClick={() => runExport('all')}>
            All filtered rows ({fmtInt(totalRowCount)} row
            {totalRowCount === 1 ? '' : 's'})
          </MenuItem>
        </Menu>

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={filters.hideZeroSpend}
              disabled={disabled}
              onChange={event => onChange({ hideZeroSpend: event.target.checked })}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: '#475569' }}>
              Hide zero spend
            </Typography>
          }
          sx={{ ml: 0 }}
        />

        <Button size="small" onClick={onReset} disabled={disabled}>
          Reset
        </Button>
      </Box>

      {ruleLabels.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}
          >
            <Typography variant="caption" sx={labelSx}>
              Filters
            </Typography>
            {ruleLabels.map((label, index) => (
              <Chip
                key={`${label}-${index}`}
                label={label}
                size="small"
                disabled={disabled}
                onDelete={() => onRemoveRule(index)}
                sx={{ backgroundColor: 'rgba(99,102,241,0.10)', color: '#4F46E5' }}
              />
            ))}
            <Button size="small" onClick={onClearRules} disabled={disabled}>
              Clear all
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
}
